import { useRouter } from 'next/router';

import { Box, Button, Card, Heading, Icon, List, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconMegaphoneFilled } from '@zillow/constellation-icons';

import { SceneIllustration } from '@zg-rentals/visual-cohesion';

import QueryConstants from '../../../constants/QueryConstants';
import { EXTERNAL_ROUTES } from '../../../constants/urls';

import { heroContainerStyles, infoContainerStyles } from './styles';

import { useAuth } from '../../AuthProvider';

export const ZRMOverview = ({
  hasAnyArchivedListings,
  handleShowArchivedClick,
}: {
  hasAnyArchivedListings: boolean;
  handleShowArchivedClick: () => void;
}) => {
  const router = useRouter();
  const { user } = useAuth();

  // Add postingPath query param to open the add property modal
  const handleAddPropertyClick = () => {
    const searchParams = new URLSearchParams(router.asPath.split('?')[1] || '');
    searchParams.set(QueryConstants.POSTING_PATH, 'true');
    router.replace({
      pathname: router.pathname,
      query: Object.fromEntries(searchParams.entries()),
    });
  };

  const renderProductOverview = () => (
    <Card elevated={false} css={infoContainerStyles}>
      <Icon size="lg" role="img" css={{ marginBottom: '600' }}>
        <IconMegaphoneFilled />
      </Icon>
      <Paragraph textStyle="body-sm-bold">Our all-in-one solution saves time</Paragraph>
      <List.Root css={{ marginBottom: '600' }}>
        <List.Item>List your property for rent</List.Item>
        <List.Item>Receive applications</List.Item>
        <List.Item>Screen tenants</List.Item>
        <List.Item>Message renters</List.Item>
        <List.Item>Create and sign leases</List.Item>
        <List.Item>Receive rent payments</List.Item>
      </List.Root>
      <TextButton asChild textStyle="body-sm-bold">
        <a href={EXTERNAL_ROUTES.ZRM_HOME_PAGE}>Learn more</a>
      </TextButton>
    </Card>
  );

  return (
    <>
      <Box css={heroContainerStyles} data-testid="ZRMOverview">
        {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 512px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
        <Box css={{ maxWidth: '512px', marginBottom: '1200' }}>
          <SceneIllustration name="rent-home" />
        </Box>
        <Heading level={3} textStyle="heading-md" css={{ marginBottom: '400' }} data-testid="fs-mask">
          {user?.firstName ? `Hi, ${user.firstName}!` : 'Hi!'} Ready to add your property?
        </Heading>
        <Paragraph textStyle="body-lg-bold" css={{ marginBottom: '400' }}>
          Providing some basic information is the first step
        </Paragraph>
        <Paragraph css={{ marginBottom: '600' }}>
          Once you find a renter for your listing, you can screen them, sign the lease, and collect rent — entirely on
          Zillow Rental Manager
        </Paragraph>
        <Button
          tone="brand"
          emphasis="filled"
          data-testid="ZeroPropertiesAddButton"
          css={{ marginBottom: '800' }}
          onClick={handleAddPropertyClick}
        >
          Add a property
        </Button>
      </Box>
      {user?.isEnterpriseCustomer && hasAnyArchivedListings ? (
        <Box>
          {renderProductOverview()}
          <Card elevated={false} css={{ ...infoContainerStyles, marginTop: '400' }}>
            <Paragraph textStyle="body-sm-bold" css={{ marginBottom: '600' }}>
              Want to activate an old property?
            </Paragraph>
            <TextButton textStyle="body-sm-bold" onClick={handleShowArchivedClick}>
              View archived properties
            </TextButton>
          </Card>
        </Box>
      ) : (
        renderProductOverview()
      )}
    </>
  );
};
